import { ReactiveController, ReactiveControllerHost } from 'lit';
import * as focusTrap from 'focus-trap';

export class FocusTrapController implements ReactiveController {
  private host: ReactiveControllerHost;

  private trap: focusTrap.FocusTrap;

  returnFocusOnDeactivate? = true;

  /** Resolves the CURRENT `preventScroll` intent of the host (see below). */
  private preventScroll: () => boolean;

  /** Trigger the trap would have returned focus to; set by `setReturnFocus`. */
  private returnNode: HTMLElement | null = null;

  /**
   * @param preventScroll  PSTUDIO-4033 — opt-in: hand focus back to the trigger
   * WITHOUT scrolling it into view. Off by default.
   *
   * The trap returns focus ~500ms after deactivation (`checkCanReturnFocus`).
   * By then the app may have deliberately scrolled elsewhere — the workflows
   * table reveals a freshly inserted block — and a plain `focus()` on a trigger
   * that is now off-screen makes the browser scroll it back, undoing that
   * reveal (PSTUDIO-3848).
   *
   * Deliberately NOT `focus-trap`'s own `preventScroll` option: that one is
   * applied in its shared `_tryFocus`, so it would also suppress the scroll on
   * the initial focus, on the pull-back when focus escapes, and on the Tab wrap
   * at a group boundary. `ps-popover` (`max-height: 70vh`), `ps-dropdown`
   * (`60vh`) and `ps-modal` all scroll internally and their menus DO outgrow
   * that height, so wrapping Tab from the last item to the first would leave
   * focus on a row the container never scrolls to — alive but invisible
   * (WCAG 2.4.7). Instead the trap is told not to return focus at all
   * (`setReturnFocus` → `false`, which `_tryFocus` short-circuits) and the
   * return is performed here, from `onPostDeactivate` — the very callback
   * `focus-trap` runs right after the return it just skipped, so the timing is
   * unchanged and only THAT one focus move loses its scroll.
   *
   * Accepts a getter because `@lit/react` assigns properties after
   * `connectedCallback`, i.e. after the trap is built; both callbacks read it
   * lazily, at deactivation time.
   */
  constructor(
    host: ReactiveControllerHost,
    returnFocusOnDeactivate = true,
    preventScroll: boolean | (() => boolean) = false
  ) {
    this.returnFocusOnDeactivate = returnFocusOnDeactivate;
    this.preventScroll =
      typeof preventScroll === 'function' ? preventScroll : () => preventScroll;
    (this.host = host).addController(this);
  }

  /**
   * The chain of active elements, piercing shadow roots — `document`'s active
   * element, then its shadow root's, and so on. `focus-trap` walks the same
   * chain for the same reason: with shadow DOM, `document.activeElement` is the
   * outermost HOST, so comparing it alone answers the wrong question.
   */
  private static activeElementChain(): Element[] {
    const chain: Element[] = [];
    let root: Document | ShadowRoot | null = document;

    while (root) {
      const active: Element | null = root.activeElement;
      if (!active || chain.includes(active)) break;
      chain.push(active);
      root = active.shadowRoot;
    }

    return chain;
  }

  /**
   * Has something OTHER than this trap and its trigger taken focus by the time
   * the return is due?
   *
   * The return fires ~500ms after deactivation (`checkCanReturnFocus`), and an
   * app can legitimately move focus inside that window: Pro Studio's validation
   * popover closes and then focuses the offending table cell. Pulling focus back
   * to the trigger there cancels the navigation the user asked for (WCAG 2.4.3)
   * and scrolls the cell they were sent to out of view (2.4.7).
   *
   * An UNfocused document is not a deliberate move: closing a popover normally
   * leaves focus on `document.body` (the focused node was removed or hidden),
   * and that is exactly the case the return exists for — Escape must still hand
   * the trigger back. So only a real, foreign focus owner counts.
   */
  private hasFocusMovedAway(): boolean {
    const chain = FocusTrapController.activeElementChain();
    const active = chain[chain.length - 1];

    if (!active || active === document.body) {
      return false;
    }

    const trapEl = this.host as unknown as HTMLElement;
    const trigger = this.returnNode;

    return !chain.some(
      (node) =>
        node === trapEl ||
        trapEl.contains(node) ||
        (!!trigger && (node === trigger || trigger.contains(node)))
    );
  }

  private createTrap() {
    const { returnFocusOnDeactivate } = this;

    // Typings here for this.host are not great but wasn't sure how to handle -- kept throwing errors
    this.trap = focusTrap.createFocusTrap(this.host as unknown as HTMLElement, {
      clickOutsideDeactivates: true,
      escapeDeactivates: true,
      setReturnFocus: (nodeFocusedBeforeActivation) => {
        // if returnFocusOnDeactivate isn't disabled, return back the original element
        if (!returnFocusOnDeactivate) {
          return false;
        }

        // Called twice per deactivation (once for `checkCanReturnFocus`, once
        // for the return itself), so this assignment must stay idempotent.
        this.returnNode = nodeFocusedBeforeActivation as HTMLElement;

        // The second call happens INSIDE the return itself, i.e. after the
        // ~500ms wait, so this is the moment to notice that the app has since
        // moved focus on purpose and to stand down (PSTUDIO-4034).
        if (this.hasFocusMovedAway()) {
          this.returnNode = null;
          return false;
        }

        // Opted in: suppress the library's own return and do it below instead.
        return this.preventScroll() ? false : nodeFocusedBeforeActivation;
      },
      onPostDeactivate: () => {
        const node = this.returnNode;
        this.returnNode = null;
        if (!returnFocusOnDeactivate || !this.preventScroll() || !node) {
          return;
        }

        // Mirrors what `_tryFocus` would have done, minus the scroll.
        if (node !== document.activeElement) {
          node.focus({ preventScroll: true });
        }
      },
      checkCanReturnFocus: () =>
        new Promise((resolve) => {
          setTimeout(() => {
            resolve();
          }, 500);
        }),

      // initialFocus: this.host as unknown as HTMLElement,
      fallbackFocus: this.host as unknown as HTMLElement,
      tabbableOptions: {
        getShadowRoot: true,
      },
    });
  }

  hostConnected() {
    this.createTrap();
  }

  hostDisconnected() {
    this.trap?.deactivate();
  }

  /**
   * Activates the focus trap.
   */
  activate() {
    this.trap.activate();
  }

  /**
   * Deactivate the focus trap.
   */
  deactivate() {
    this.trap?.deactivate();
  }
}
